import { useListProjectsQuery } from '../api/project';
import { useTenantData } from './useTenantData';
import { useProfileData } from './useProfileData';
import { useGetUserQuery } from '../api/user';
import { getFeatureLimit, isFeatureUnlimited, isFeatureAvailable, FEATURE_KEYS } from '../constants/subscriptionTiers';

// Base hook that provides common data for all permission checks
export const usePermissionData = () => {
    const { data: projects = [] } = useListProjectsQuery({});
    const { tenantData } = useTenantData();
    const { profileData, isProfileComplete } = useProfileData();

    return {
        projects,
        tenantData,
        profileData,
        subscription: tenantData?.subscription || 'free',
        seatAllocation: tenantData?.seatAllocation,
        isProfileComplete
    };
};

// 1. Project Creation Permission Hook
export const useCanCreateProject = () => {
    const { projects, subscription, isProfileComplete } = usePermissionData();
    
    const currentProjectCount = projects.length;
    const projectLimit = getFeatureLimit(subscription, FEATURE_KEYS.PROJECT_CREATION);
    const isProjectLimitUnlimited = isFeatureUnlimited(subscription, FEATURE_KEYS.PROJECT_CREATION);
    const isProjectLimitReached = !isProjectLimitUnlimited && projectLimit && 
        currentProjectCount >= parseInt(projectLimit.toString().split(' ')[0] || '0');
    
    const canCreateProject = isProfileComplete && !isProjectLimitReached;
    
    const getProjectCreationTooltip = () => {
        if (!isProfileComplete) return 'Complete your profile first';
        if (isProjectLimitReached) return `Project limit reached (${currentProjectCount}/${projectLimit}). Upgrade your plan to create more projects!`;
        return '';
    };

    return {
        canCreateProject,
        currentProjectCount,
        projectLimit,
        isProjectLimitUnlimited,
        isProjectLimitReached,
        isProfileComplete,
        getProjectCreationTooltip
    };
};

// 2. Reporting Feature Permission Hook
export const useCanUseReporting = () => {
    const { subscription } = usePermissionData();
    
    const canUseReporting = isFeatureAvailable(subscription, FEATURE_KEYS.REPORTING);
    const reportingLimit = getFeatureLimit(subscription, FEATURE_KEYS.REPORTING);
    const isReportingUnlimited = isFeatureUnlimited(subscription, FEATURE_KEYS.REPORTING);
    
    const getReportingTooltip = () => {
        if (!canUseReporting) return 'Reporting feature not available in your current plan. Upgrade to access reporting!';
        if (isReportingUnlimited) return 'Unlimited reporting available';
        return `Reporting limit: ${reportingLimit}`;
    };

    return {
        canUseReporting,
        reportingLimit,
        isReportingUnlimited,
        getReportingTooltip
    };
};

// 3. Image Upload Permission Hook
export const useCanUploadImages = () => {
    const { tenantData, subscription } = usePermissionData();
    
    const currentImageUsage = tenantData?.imageUsage?.used || 0;
    const imageQuota = tenantData?.imageUsage?.quota || 0;
    const imageLimit = getFeatureLimit(subscription, FEATURE_KEYS.IMAGE_UPLOAD);
    const isImageLimitUnlimited = isFeatureUnlimited(subscription, FEATURE_KEYS.IMAGE_UPLOAD);
    
    // Use actual quota if available, otherwise fall back to tier limit
    const effectiveLimit = imageQuota > 0 ? imageQuota : 
        (typeof imageLimit === 'string' ? parseInt(imageLimit.toString().split(' ')[0] || '0') : imageLimit);
    
    const isImageLimitReached = !isImageLimitUnlimited && currentImageUsage >= effectiveLimit;
    const canUploadImages = !isImageLimitReached;
    
    const getImageUploadTooltip = () => {
        if (isImageLimitReached) return `Image upload limit reached (${currentImageUsage}/${effectiveLimit}). Upgrade your plan to upload more images!`;
        if (isImageLimitUnlimited) return 'Unlimited image uploads available';
        return `Image uploads: ${currentImageUsage}/${effectiveLimit}`;
    };

    return {
        canUploadImages,
        currentImageUsage,
        imageQuota,
        imageLimit,
        effectiveLimit,
        isImageLimitUnlimited,
        isImageLimitReached,
        getImageUploadTooltip
    };
};

// 4. Analysis Submission Permission Hook
export const useCanSubmitAnalysis = () => {
    const { tenantData, subscription } = usePermissionData();
    
    const currentAnalysisUsage = tenantData?.analysisUsage?.used || 0;
    const analysisQuota = tenantData?.analysisUsage?.quota || 0;
    const analysisLimit = getFeatureLimit(subscription, FEATURE_KEYS.IMAGE_ANALYSIS);
    const isAnalysisLimitUnlimited = isFeatureUnlimited(subscription, FEATURE_KEYS.IMAGE_ANALYSIS);
    
    // Use actual quota if available, otherwise fall back to tier limit
    const effectiveLimit = analysisQuota > 0 ? analysisQuota : 
        (typeof analysisLimit === 'string' ? parseInt(analysisLimit.toString().split(' ')[0] || '0') : analysisLimit);
    
    const isAnalysisLimitReached = !isAnalysisLimitUnlimited && currentAnalysisUsage >= effectiveLimit;
    const canSubmitAnalysis = !isAnalysisLimitReached;
    
    const getAnalysisTooltip = () => {
        if (isAnalysisLimitReached) return `Analysis limit reached (${currentAnalysisUsage}/${effectiveLimit}). Upgrade your plan to analyze more images!`;
        if (isAnalysisLimitUnlimited) return 'Unlimited analysis available';
        return `Analysis usage: ${currentAnalysisUsage}/${effectiveLimit}`;
    };

    return {
        canSubmitAnalysis,
        currentAnalysisUsage,
        analysisQuota,
        analysisLimit,
        effectiveLimit,
        isAnalysisLimitUnlimited,
        isAnalysisLimitReached,
        getAnalysisTooltip
    };
};

// 5. Team Management Permission Hook
export const useCanManageTeam = () => {
    const { tenantData, subscription } = usePermissionData();
    
    const currentSeatsUsed = tenantData?.seatAllocation?.used || 0;
    const seatQuota = tenantData?.seatAllocation?.quota || 0;
    const teamLimit = getFeatureLimit(subscription, FEATURE_KEYS.TEAM_COLLABORATION);
    const isTeamLimitUnlimited = isFeatureUnlimited(subscription, FEATURE_KEYS.TEAM_COLLABORATION);
    
    // Use actual quota if available, otherwise fall back to tier limit
    const effectiveLimit = seatQuota > 0 ? seatQuota : 
        (typeof teamLimit === 'string' ? parseInt(teamLimit.toString().split(' ')[0] || '0') : teamLimit);
    
    const isTeamLimitReached = !isTeamLimitUnlimited && currentSeatsUsed >= effectiveLimit;
    
    // Check if user has admin role (this will be passed from the component)
const hasAdminRole = (userRole?: string) => userRole === 'Administrator';
    
    // Can manage team if: has admin role AND has more than 1 seat AND not at limit
    const canManageTeam = (userRole?: string) => hasAdminRole(userRole) && effectiveLimit > 1 && !isTeamLimitReached;
    const canInviteMembers = (userRole?: string) => hasAdminRole(userRole) && effectiveLimit > 1 && !isTeamLimitReached;
    
    const getTeamManagementTooltip = () => {
        if (effectiveLimit <= 1) return 'Team management requires more than 1 seat. Upgrade your plan!';
        if (isTeamLimitReached) return `Team limit reached (${currentSeatsUsed}/${effectiveLimit}). Upgrade your plan to add more team members!`;
        if (isTeamLimitUnlimited) return 'Unlimited team members available';
        return `Team members: ${currentSeatsUsed}/${effectiveLimit}`;
    };

    return {
        canManageTeam,
        canInviteMembers,
        hasAdminRole,
        currentSeatsUsed,
        seatQuota,
        teamLimit,
        effectiveLimit,
        isTeamLimitUnlimited,
        isTeamLimitReached,
        getTeamManagementTooltip
    };
};

// 6. API Access Permission Hook
export const useCanUseAPI = () => {
    const { subscription } = usePermissionData();
    
    const canUseAPI = isFeatureAvailable(subscription, FEATURE_KEYS.API_ACCESS);
    const apiLimit = getFeatureLimit(subscription, FEATURE_KEYS.API_ACCESS);
    const isAPILimitUnlimited = isFeatureUnlimited(subscription, FEATURE_KEYS.API_ACCESS);
    
    const getAPITooltip = () => {
        if (!canUseAPI) return 'API access not available in your current plan. Upgrade to access API!';
        if (isAPILimitUnlimited) return 'Unlimited API access available';
        return `API limit: ${apiLimit}`;
    };

    return {
        canUseAPI,
        apiLimit,
        isAPILimitUnlimited,
        getAPITooltip
    };
};

// 7. Account Management Permission Hook
export const useCanManageAccount = () => {
    const { data: userData } = useGetUserQuery(undefined);
    
    // Can manage account if user has Administrator role
    const canManageAccount = userData?.role === 'Administrator';
    
    const getAccountManagementTooltip = () => {
        if (!canManageAccount) return 'Only administrators can manage account settings';
        return '';
    };
    
    return {
        canManageAccount,
        getAccountManagementTooltip
    };
};

// 8. Comprehensive Permission Hook (combines all permissions)
export const usePermissions = () => {
    const projectPermissions = useCanCreateProject();
    const reportingPermissions = useCanUseReporting();
    const imagePermissions = useCanUploadImages();
    const analysisPermissions = useCanSubmitAnalysis();
    const teamPermissions = useCanManageTeam();
    const apiPermissions = useCanUseAPI();
    const accountPermissions = useCanManageAccount();

    return {
        // Individual permission hooks
        project: projectPermissions,
        reporting: reportingPermissions,
        images: imagePermissions,
        analysis: analysisPermissions,
        team: teamPermissions,
        api: apiPermissions,
        account: accountPermissions,
        
        // Summary permissions
        canCreateProject: projectPermissions.canCreateProject,
        canUseReporting: reportingPermissions.canUseReporting,
        canUploadImages: imagePermissions.canUploadImages,
        canSubmitAnalysis: analysisPermissions.canSubmitAnalysis,
        canManageTeam: teamPermissions.canManageTeam,
        canUseAPI: apiPermissions.canUseAPI,
        canManageAccount: accountPermissions.canManageAccount,
        
        // All tooltips
        tooltips: {
            project: projectPermissions.getProjectCreationTooltip,
            reporting: reportingPermissions.getReportingTooltip,
            images: imagePermissions.getImageUploadTooltip,
            analysis: analysisPermissions.getAnalysisTooltip,
            team: teamPermissions.getTeamManagementTooltip,
            api: apiPermissions.getAPITooltip,
            account: accountPermissions.getAccountManagementTooltip
        }
    };
};
